/**
 * SPLH Performance Dashboard V2
 *
 * Enhanced version of the SPLH Dashboard with comparison mode support.
 * Provides period-over-period analysis with Previous Period, Previous Month,
 * and Previous Year comparison options.
 *
 * Features:
 * - Comparison mode selector (Previous Period, Previous Month, Previous Year)
 * - Dual-series trend chart
 * - Delta percentages on all metrics
 * - AI-generated insights (non-blocking)
 * - Grouped alerts by metric type
 * - Enhanced breakdown with comparison
 *
 * @module pages/common/dashboards/SPLHDashboard
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

// Layout components
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';

// Dashboard hooks
import { useSPLHDataV2, COMPARISON_MODES, DEFAULT_COMPARISON_MODE } from './hooks/useSPLHDataV2';
import { useHierarchyNavigation } from './hooks';

// Dashboard utilities
import { getPresetDateRange } from './utils/dateUtils';

// Dashboard services
import { filterHierarchyForDisplay, getDisplayLevel } from './services/hierarchyService';

// Dashboard config
import { getSPLHConfig } from './config';

// Dashboard constants
import { DEFAULT_CLIENT_ID, DEFAULT_DATE_RANGE_PRESET } from './constants';

// Dashboard components (V2)
import HeaderControls from './components/HeaderControls';
import KPIRowV2 from './components/KPIRowV2';
import TrendChartV2 from './components/TrendChartV2';
import HierarchyTableV2 from './components/HierarchyTableV2';
import BreakdownSectionV2 from './components/BreakdownSectionV2';
import AlertsPanelV2 from './components/AlertsPanelV2';
import HierarchyBreadcrumb from './components/HierarchyBreadcrumb';
import AIInsights from './components/AIInsights';

/**
 * SPLH Performance Dashboard V2 Component
 *
 * Displays labor efficiency metrics with comparison support:
 * - Date range filters with comparison mode selector
 * - KPI summary cards with delta percentages
 * - Grouped performance alerts
 * - Dual-series SPLH trend chart
 * - Hierarchy drilldown table with trend arrows
 * - Breakdown analysis with comparison
 * - AI-generated insights
 *
 * @returns {React.Component} SPLH Dashboard V2 component
 */
const SPLHDashboardV2 = () => {
  // Client ID - hardcoded to 'dem50' for initial implementation
  const clientId = DEFAULT_CLIENT_ID;

  // Load frontend config for the client
  const config = useMemo(() => getSPLHConfig(clientId), [clientId]);

  // Date range state
  const [dateRangePreset, setDateRangePreset] = useState(DEFAULT_DATE_RANGE_PRESET);
  const [customDateRange, setCustomDateRange] = useState(null);

  // Calculate the actual date range from preset or custom selection
  const dateRange = useMemo(() => {
    try {
      return getPresetDateRange(dateRangePreset, customDateRange);
    } catch {
      return getPresetDateRange('last7days');
    }
  }, [dateRangePreset, customDateRange]);

  // Fetch dashboard data using the V2 hook with comparison support
  const {
    comparisonMode,
    setComparisonMode,
    currentRange,
    comparisonRange,
    kpis,
    trendData,
    hierarchyData,
    breakdownData,
    alerts,
    hierarchyMetadata,
    aiInsights,
    aiLoading,
    loading,
    error,
    refetch,
    refetchAI,
  } = useSPLHDataV2(clientId, dateRange, DEFAULT_COMPARISON_MODE);

  // Debug logging for V2 data
  useMemo(() => {
    if (hierarchyData?.length > 0) {
      console.log('[SPLHDashboardV2] Dashboard data loaded:', {
        comparisonMode,
        currentRange,
        comparisonRange,
        kpisCount: kpis?.length,
        trendDataPoints: trendData?.length,
        hierarchyNodes: hierarchyData?.length,
        hasAlerts: !!alerts,
        aiAvailable: aiInsights?.available,
      });
    }
  }, [hierarchyData, comparisonMode, currentRange, comparisonRange, kpis, trendData, alerts, aiInsights]);

  // Hierarchy navigation state
  const {
    path: hierarchyPath,
    breadcrumb,
    canGoBack,
    isAtRoot,
    hasHierarchy,
    highestLevel,
    drilldown,
    goBack,
    goToLevel,
    reset: resetHierarchy,
  } = useHierarchyNavigation(hierarchyMetadata);

  // Filter hierarchy data based on current navigation level
  const filteredHierarchyData = useMemo(() => {
    return filterHierarchyForDisplay(hierarchyData, hierarchyPath, hierarchyMetadata);
  }, [hierarchyData, hierarchyPath, hierarchyMetadata]);

  // Get the current display level info
  const displayLevel = useMemo(() => {
    return getDisplayLevel(hierarchyPath, hierarchyMetadata);
  }, [hierarchyPath, hierarchyMetadata]);

  /**
   * Handles date range preset changes
   */
  const handleDateRangePresetChange = useCallback((preset) => {
    setDateRangePreset(preset);
    if (preset !== 'custom') {
      setCustomDateRange(null);
    }
  }, []);

  /**
   * Handles date range selection from modal (includes preset)
   */
  const handleDateRangeChange = useCallback((selection) => {
    const { startDate, endDate, preset } = selection;
    if (preset && preset !== 'custom') {
      // Preset was selected
      setDateRangePreset(preset);
      setCustomDateRange(null);
    } else {
      // Custom range was selected
      setCustomDateRange({ startDate, endDate });
      setDateRangePreset('custom');
    }
  }, []);

  /**
   * Resets filters to default values
   */
  const handleResetFilters = useCallback(() => {
    setDateRangePreset(DEFAULT_DATE_RANGE_PRESET);
    setCustomDateRange(null);
    setComparisonMode(DEFAULT_COMPARISON_MODE);
  }, [setComparisonMode]);

  /**
   * Handles row clicks in hierarchy table for drilldown
   */
  const handleHierarchyDrilldown = useCallback(
    (node) => {
      if (node?.hasChildren) {
        drilldown(node);
      }
    },
    [drilldown]
  );

  /**
   * Handles breadcrumb navigation
   */
  const handleBreadcrumbNavigate = useCallback(
    (index) => {
      if (index === -1) {
        resetHierarchy();
      } else {
        goToLevel(index);
      }
    },
    [resetHierarchy, goToLevel]
  );

  // Color configuration
  const chartColors = {
    current: config.display.chartColors.splh,
    comparison: '#9CA3AF',
    target: config.display.chartColors.target,
    positive: config.display.chartColors.positive,
    negative: config.display.chartColors.negative,
    neutral: config.display.chartColors.neutral,
    primary: config.display.chartColors.splh,
  };

  // Render loading state
  if (loading && !kpis?.length) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={3} pb={3} sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Card>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '40vh',
                gap: 2,
                p: 3,
              }}
            >
              <CircularProgress size={48} sx={{ color: config.display.chartColors.splh }} />
              <Typography variant="body1" color="text.secondary">
                Loading dashboard data...
              </Typography>
            </Box>
          </Card>
        </Box>
      </DashboardLayout>
    );
  }

  // Render error state
  if (error && !kpis?.length) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={3} pb={3} sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Card>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '40vh',
                gap: 2,
                p: 3,
              }}
            >
              <Alert
                severity="error"
                sx={{ maxWidth: 500, width: '100%' }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={refetch}
                  >
                    Retry
                  </Button>
                }
              >
                {error}
              </Alert>
            </Box>
          </Card>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box pt={3} pb={3} sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Card>
          <Box p={3}>
            {/* Dashboard Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 3,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              {/* Left side - Title */}
              <Box>
                <Typography variant="h3" fontWeight="bold">
                  SPLH Performance Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sales Per Labor Hour metrics with comparison analysis
                </Typography>
              </Box>

              {/* Right side - Date Range and Comparison controls */}
              <HeaderControls
                selectedPreset={dateRangePreset}
                dateRange={dateRange}
                comparisonMode={comparisonMode}
                comparisonRange={comparisonRange}
                onDateRangeChange={handleDateRangeChange}
                onComparisonModeChange={setComparisonMode}
                loading={loading}
              />
            </Box>

            {/* Main Dashboard Grid Layout */}
            <Grid container spacing={3}>

              {/* Row 1: KPI Summary Cards */}
              <Grid item xs={12}>
                <KPIRowV2
                  kpis={kpis}
                  loading={loading}
                  colors={chartColors}
                />
              </Grid>

              {/* Row 2: RadixDash Intelligence & Alerts side by side */}
              <Grid item xs={12} lg={6}>
                <AIInsights
                  insights={aiInsights}
                  loading={aiLoading}
                  onRefresh={refetchAI}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
                    Performance Alerts
                  </Typography>
                  <AlertsPanelV2
                    alerts={alerts}
                    loading={loading}
                  />
                </Paper>
              </Grid>

              {/* Row 3: Trend Chart - Full Width */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
                    SPLH Trend
                  </Typography>
                  <TrendChartV2
                    trendData={trendData}
                    currentRange={currentRange}
                    comparisonRange={comparisonRange}
                    targetSPLH={config.display.targetSPLH}
                    loading={loading}
                    colors={{
                      current: chartColors.current,
                      comparison: chartColors.comparison,
                      target: chartColors.target,
                    }}
                    height="280px"
                  />
                </Paper>
              </Grid>

              {/* Row 4: Hierarchy Table */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  {/* Breadcrumb Navigation */}
                  {hasHierarchy && !isAtRoot && (
                    <HierarchyBreadcrumb
                      breadcrumb={breadcrumb}
                      hierarchyMetadata={hierarchyMetadata}
                      onNavigate={handleBreadcrumbNavigate}
                    />
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {canGoBack && (
                        <Button
                          size="small"
                          onClick={goBack}
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          ← Back
                        </Button>
                      )}
                      <Typography variant="subtitle1" fontWeight="medium">
                        {displayLevel?.displayName || 'Location'} Performance
                      </Typography>
                    </Box>
                  </Box>
                  <HierarchyTableV2
                    data={filteredHierarchyData}
                    hierarchyMetadata={hierarchyMetadata}
                    currentLevel={displayLevel?.level || highestLevel?.level}
                    onDrilldown={handleHierarchyDrilldown}
                    loading={loading}
                    colors={{
                      positive: chartColors.positive,
                      negative: chartColors.negative,
                      neutral: chartColors.neutral,
                    }}
                  />
                </Paper>
              </Grid>

              {/* Row 5: Breakdown Section */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
                    Breakdown Analysis
                  </Typography>
                  <BreakdownSectionV2
                    breakdownData={breakdownData}
                    loading={loading}
                    colors={{
                      positive: chartColors.positive,
                      negative: chartColors.negative,
                      neutral: chartColors.neutral,
                    }}
                  />
                </Paper>
              </Grid>
            </Grid>

            {/* Refresh indicator */}
            {loading && kpis?.length > 0 && (
              <Box
                sx={{
                  position: 'fixed',
                  bottom: 24,
                  right: 24,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'background.paper',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2">Refreshing...</Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Box>
    </DashboardLayout>
  );
};

export default SPLHDashboardV2;
